import { createContext, type ReactNode, useContext } from "react";

/**
 * Tells the detail slot whether it currently occupies the full width of the
 * split view — i.e. the list pane is collapsed and only the detail is showing.
 * This is true when the effective mode is "detail" (either an explicit
 * `mode="detail"` or a responsive collapse below `Screen.md`).
 *
 * Unlike a viewport media query, this reflects the real pane geometry, so a
 * detail slot can adapt its chrome (e.g. drop a window-level "expand to page"
 * or "close" affordance that only makes sense when the list is beside it).
 *
 * `false` outside a provider, so `useIsDetailFullWidth` is safe to call from a
 * component that may render outside any split view (page mode, modal, etc.).
 */
const DetailFullWidthContext = createContext<boolean>(false);

interface IDetailFullWidthProviderProps {
  /** True when the detail pane is the only visible pane (list collapsed). */
  isFullWidth: boolean;
  children: ReactNode;
}

export const DetailFullWidthProvider = ({
  isFullWidth,
  children,
}: IDetailFullWidthProviderProps) => (
  <DetailFullWidthContext.Provider value={isFullWidth}>
    {children}
  </DetailFullWidthContext.Provider>
);

/** True when rendered inside a split-view detail pane that fills the whole view. */
export const useIsDetailFullWidth = (): boolean =>
  useContext(DetailFullWidthContext);
